import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Search, MapPin, Briefcase, Clock, Home as HomeIcon, DollarSign,
  Bookmark, Share2, ExternalLink, X,
} from 'lucide-react';
import JobastiLogo from '../components/JobastiLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import { useAuth } from '../contexts/AuthContext';

const CURRENCY = { EUR: '€', USD: '$', GBP: '£' };

const formatSalary = (p) => {
  const sym = CURRENCY[p.salary_currency] || p.salary_currency || '';
  if (p.salary_min && p.salary_max) return `${sym}${(p.salary_min / 1000).toFixed(0)}k – ${sym}${(p.salary_max / 1000).toFixed(0)}k`;
  if (p.salary_min) return `${sym}${(p.salary_min / 1000).toFixed(0)}k+`;
  if (p.salary_max) return `up to ${sym}${(p.salary_max / 1000).toFixed(0)}k`;
  return null;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60 * 60) return 'just posted';
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400 / 7)}w ago`;
  return `${Math.floor(diff / 86400 / 30)}mo ago`;
};

const PublicJobsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isProspect = ['prospect', 'PROS'].includes(userProfile?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [q, setQ] = useState(searchParams.get('q') || '');
  const [loc, setLoc] = useState(searchParams.get('loc') || '');
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [savedIds, setSavedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('jobasti_saved_jobs') || '[]')); }
    catch { return new Set(); }
  });

  const fetchJobs = async (params) => {
    setLoading(true);
    try {
      const usp = new URLSearchParams();
      if (params.q) usp.set('q', params.q);
      if (params.loc) usp.set('loc', params.loc);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/positions?${usp.toString()}`;
      const r = await axios.get(url);
      const list = r.data?.data?.positions || [];
      setPositions(list);
      if (list.length && (!selectedId || !list.find(p => p.id === selectedId))) {
        setSelectedId(list[0].id);
      } else if (!list.length) {
        setSelectedId(null);
      }
    } catch (e) {
      console.error('Public positions fetch failed', e);
      setPositions([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  };

  // Initial + when URL params change (deep-linkable)
  useEffect(() => {
    fetchJobs({
      q: searchParams.get('q') || '',
      loc: searchParams.get('loc') || '',
    });
  }, [searchParams]);

  const onSubmit = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams();
    if (q.trim()) next.set('q', q.trim());
    if (loc.trim()) next.set('loc', loc.trim());
    setSearchParams(next);
  };

  const clearSearch = () => {
    setQ(''); setLoc('');
    setSearchParams(new URLSearchParams());
  };

  const selected = useMemo(() => positions.find(p => p.id === selectedId) || null, [positions, selectedId]);

  const toggleSave = (id) => {
    setSavedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem('jobasti_saved_jobs', JSON.stringify(Array.from(next))); } catch { /* noop */ }
      return next;
    });
  };

  const openFullJob = (p) => {
    // Logged-in prospect → in-app job detail (with prefilled apply modal).
    if (isProspect) {
      navigate(`/jobs/${p.id}`);
      return;
    }
    // Everyone else → the public company microsite job page.
    const slug = p?.organization?.slug;
    if (slug) navigate(`/${slug}/jobs/${p.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif' }} data-testid="public-jobs-list">
      {/* NAV — use the app shell for logged-in prospects, public header otherwise */}
      {isProspect ? (
        <ResponsiveNavigation />
      ) : (
        <header className="bg-white sticky top-0 z-30 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center" data-testid="pj-logo">
              <JobastiLogo height={32} />
            </Link>
            <nav className="hidden md:flex items-center gap-7 text-sm text-gray-800">
              <Link to="/" className="text-blue-500 font-medium">{t('seekerNav.jobSearch')}</Link>
              <a href="mailto:support@jobasti.com" className="hover:text-blue-500">{t('seekerNav.helpCenter')}</a>
              <Link to="/for-companies" className="hover:text-blue-500">{t('seekerNav.forCompanies')}</Link>
            </nav>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link to="/login" className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
                {t('common.loginArrow')}
              </Link>
            </div>
          </div>
        </header>
      )}

      {/* SEARCH BAR (Indeed-style) */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
          <form onSubmit={onSubmit} className="flex flex-col sm:flex-row items-stretch bg-white rounded-full sm:rounded-full shadow-sm border-2 border-gray-900/90 focus-within:border-blue-500 transition-colors overflow-hidden">
            {/* Keyword */}
            <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5">
              <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="text"
                placeholder={t('seekerHero.searchPlaceholder')}
                className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                data-testid="pj-search-q"
              />
              {q && (
                <button type="button" onClick={() => setQ('')} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear keyword">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {/* Vertical divider */}
            <div className="hidden sm:block w-px bg-gray-200 my-3" />
            {/* Location */}
            <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5 border-t border-gray-100 sm:border-t-0">
              <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <input
                value={loc}
                onChange={(e) => setLoc(e.target.value)}
                type="text"
                placeholder="City, region or remote"
                className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                data-testid="pj-search-loc"
              />
              {loc && (
                <button type="button" onClick={() => setLoc('')} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear location">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {/* Find jobs */}
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm px-8 py-3.5 sm:m-2 rounded-full transition-colors"
              data-testid="pj-search-submit"
            >
              {loading ? t('common.sending') : 'Find jobs'}
            </button>
          </form>

          {/* Applied filters chip row */}
          {(searchParams.get('q') || searchParams.get('loc')) && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span>Filtered by:</span>
              {searchParams.get('q') && (
                <span className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-3 py-1">
                  <Search className="w-3 h-3" /> {searchParams.get('q')}
                </span>
              )}
              {searchParams.get('loc') && (
                <span className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-3 py-1">
                  <MapPin className="w-3 h-3" /> {searchParams.get('loc')}
                </span>
              )}
              <button onClick={clearSearch} className="text-blue-500 hover:text-blue-600 font-medium ml-1" data-testid="pj-clear-filters">Clear all</button>
              <span className="ml-auto text-gray-500">
                {loading ? '…' : `${positions.length} result${positions.length === 1 ? '' : 's'}`}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* SPLIT VIEW: list left, detail right */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {loading ? (
          <div className="text-center py-20 text-gray-500 text-sm">Loading jobs…</div>
        ) : positions.length === 0 ? (
          <EmptyState q={searchParams.get('q')} loc={searchParams.get('loc')} onClear={clearSearch} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6" data-testid="pj-split-view">
            {/* LEFT COLUMN: job cards */}
            <div className="flex flex-col gap-3" data-testid="pj-list-column">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-1">
                Jobs for you
              </h2>
              {positions.map((p) => {
                const isActive = p.id === selectedId;
                const saved = savedIds.has(p.id);
                const salary = formatSalary(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedId(p.id)}
                    className={`text-left bg-white rounded-2xl border p-5 transition-all ${
                      isActive
                        ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                    data-testid={`pj-card-${p.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        {timeAgo(p.published_at) === 'just posted' && (
                          <div className="mb-1.5">
                            <span className="inline-flex items-center bg-emerald-50 text-emerald-700 text-[11px] font-medium px-2 py-0.5 rounded-full">
                              New
                            </span>
                          </div>
                        )}
                        <h3 className="text-[15px] font-semibold text-gray-900 leading-snug line-clamp-2">{p.title}</h3>
                        <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1 truncate">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                          {p.location || 'Location TBC'}
                        </p>
                        {salary && (
                          <p className="text-sm text-gray-900 font-medium mt-2">{salary}</p>
                        )}
                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                          {p.employment_type && (
                            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[11px] font-medium px-2 py-0.5 rounded-full">
                              <Clock className="w-3 h-3" /> {p.employment_type}
                            </span>
                          )}
                          {p.workplace && (
                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-[11px] px-2 py-0.5 rounded-full">
                              <HomeIcon className="w-3 h-3" /> {p.workplace}
                            </span>
                          )}
                          {p.experience_level && (
                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-[11px] px-2 py-0.5 rounded-full">
                              <Briefcase className="w-3 h-3" /> {p.experience_level}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleSave(p.id); }}
                          className={`p-1.5 rounded-full transition-colors ${saved ? 'text-blue-500 hover:bg-blue-50' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'}`}
                          aria-label={saved ? 'Unsave' : 'Save'}
                          data-testid={`pj-save-${p.id}`}
                        >
                          <Bookmark className={`w-4 h-4 ${saved ? 'fill-blue-500' : ''}`} />
                        </button>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-3">{timeAgo(p.published_at)}</p>
                  </button>
                );
              })}
            </div>

            {/* RIGHT COLUMN: sticky detail panel */}
            <div className="hidden lg:block" data-testid="pj-detail-column">
              {selected && <DetailPanel job={selected} onOpenFull={openFullJob} />}
            </div>
          </div>
        )}
      </section>

      {/* Mobile detail overlay */}
      {selected && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 shadow-2xl p-4 flex items-center gap-3 z-40">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{selected.title}</p>
            <p className="text-xs text-gray-500 truncate flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {selected.location || 'Location TBC'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => openFullJob(selected)}
            className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm px-5 py-2 rounded-lg"
          >
            Apply
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ q, loc, onClear }) => (
  <div className="bg-white border border-gray-200 rounded-2xl px-8 py-16 text-center max-w-2xl mx-auto" data-testid="pj-empty">
    <div className="w-16 h-16 mx-auto rounded-full bg-blue-50 flex items-center justify-center mb-4">
      <Search className="w-7 h-7 text-blue-500" />
    </div>
    <h3 className="text-xl font-bold text-gray-900">No jobs matching that search</h3>
    <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto leading-relaxed">
      {`We couldn't find any roles for `}
      {q && <span className="font-semibold">{`"${q}"`}</span>}
      {q && loc && ' in '}
      {loc && <span className="font-semibold">{`"${loc}"`}</span>}
      {!q && !loc && ' the applied filters'}
      {`. Try a broader keyword, remove the location, or clear all filters.`}
    </p>
    <button
      type="button"
      onClick={onClear}
      className="mt-6 inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm"
      data-testid="pj-empty-clear"
    >
      Clear filters
    </button>
  </div>
);

const DetailPanel = ({ job, onOpenFull }) => {
  const salary = formatSalary(job);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-7 sticky top-24 max-h-[calc(100vh-140px)] overflow-y-auto">
      <div className="flex items-start justify-between gap-3 pb-5 border-b border-gray-100">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900 leading-tight">{job.title}</h2>
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {job.location || 'Location TBC'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpenFull(job)}
          className="p-2 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100"
          title="Open full page"
          aria-label="Open full page"
          data-testid="pj-detail-open-full"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      {/* Key facts */}
      <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
        {salary && (
          <div className="flex items-center gap-2 text-gray-700">
            <DollarSign className="w-4 h-4 text-blue-500" />
            <span className="font-medium">{salary}</span>
          </div>
        )}
        {job.employment_type && (
          <div className="flex items-center gap-2 text-gray-700">
            <Clock className="w-4 h-4 text-blue-500" />
            <span>{job.employment_type}</span>
          </div>
        )}
        {job.workplace && (
          <div className="flex items-center gap-2 text-gray-700">
            <HomeIcon className="w-4 h-4 text-blue-500" />
            <span>{job.workplace}</span>
          </div>
        )}
        {job.experience_level && (
          <div className="flex items-center gap-2 text-gray-700">
            <Briefcase className="w-4 h-4 text-blue-500" />
            <span>{job.experience_level}</span>
          </div>
        )}
      </div>

      {/* Apply CTA */}
      <div className="flex items-center gap-3 mt-6">
        <button
          type="button"
          onClick={() => onOpenFull(job)}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm px-5 py-3 rounded-lg shadow-sm transition-colors"
          data-testid="pj-detail-apply"
        >
          Apply
          <ExternalLink className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => { try { navigator.share?.({ title: job.title, url: window.location.href }); } catch { /* noop */ } }}
          className="p-2.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50"
          aria-label="Share"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>

      {/* Description sections */}
      {job.description && (
        <div className="mt-7">
          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">About the role</h4>
          <div
            className="text-sm text-gray-700 mt-2 leading-relaxed prose prose-sm prose-slate max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5"
            dangerouslySetInnerHTML={{ __html: job.description }}
          />
        </div>
      )}
      {job.responsibilities && (
        <div className="mt-6">
          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Responsibilities</h4>
          <div
            className="text-sm text-gray-700 mt-2 leading-relaxed prose prose-sm prose-slate max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5"
            dangerouslySetInnerHTML={{ __html: job.responsibilities }}
          />
        </div>
      )}
      {Array.isArray(job.must_have_skills) && job.must_have_skills.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Must-have skills</h4>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {job.must_have_skills.map((s, i) => (
              <span key={i} className="bg-gray-100 text-gray-800 text-xs px-2.5 py-1 rounded-full">{s}</span>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(job.nice_to_have_skills) && job.nice_to_have_skills.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Nice to have</h4>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {job.nice_to_have_skills.map((s, i) => (
              <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full">{s}</span>
            ))}
          </div>
        </div>
      )}
      {job.benefits && (
        <div className="mt-6">
          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Benefits</h4>
          <div
            className="text-sm text-gray-700 mt-2 leading-relaxed prose prose-sm prose-slate max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5"
            dangerouslySetInnerHTML={{ __html: job.benefits }}
          />
        </div>
      )}
    </div>
  );
};

export default PublicJobsList;
